import React from 'react';
import escape from 'lodash.escape';
import { trackV2 } from '../../../services/common-utils';
import { dtlSyi, PriceGuidance } from '../../../services/dtl-syi';
import { SubmitButton } from '../../../services/form';
import { getPageStateThunk, updateSyi } from '../../../reducers/syi';
import httpMetrics from '@gumtree/web-bff/src/metrics/modules/http-metrics';

export default class VrnLookup {
    /**
     * @property {object} options  - Default options
     */
    options = {
        updateChannel: 'syi',
    };

    get categoryId() {
        return (document.querySelector('[name="categoryId"]') as HTMLInputElement).value;
    }
    get el() {
        const formEl = dtlSyi.form!.el;
        return formEl.querySelector('.panel.vehicle-spec-root') as HTMLFieldSetElement;
    }
    get elSubmit() {
        return this.el.querySelector('.vehicle-spec-lookup') as HTMLElement; // [data-vrn-lookup]
    }
    get elLicense() {
        return this.el.querySelector(`input[name="attributes[vrn]"]`) as HTMLInputElement;
    }
    get elManualVrnMileage() {
        return document.getElementById('vehicle_mileage') as HTMLInputElement | null;
    }
    get elVrnMileage() {
        return document.getElementById('attributes[vehicle_mileage]') as HTMLInputElement | null;
    }
    get elVrnSummary() {
        return this.el.querySelector('.vehicle-spec-summary') as HTMLDivElement; // [data-vrn-summary]
    }
    get elValidation() {
        return this.el.querySelector('.vehicle-spec-validation') as HTMLParagraphElement; // [data-vrn-validation]
    }
    get fetchUrl() {
        return '/ajax/vrn'; // 🚧
    }
    get mileageData() {
        return document.getElementById('isEstimated');
    }
    submitButton!: SubmitButton;

    // elVrnMileage = { value: null };
    // elManualVrnMileage = { value: null };

    addToTitle(attrs: VrnResponseAttributes) {
        const title = document.querySelector('#post-ad_title') as HTMLInputElement;
        const titleString = [] as string[];

        const filters = [
            'vrn',
            'vehicle_colour',
            'vehicle_estimated_mileage',
            'vehicle_fuel_type',
            'vehicle_mileage',
        ];

        const filtered = Object.keys(attrs)
            .filter((key) => !filters.includes(key))
            .reduce((obj, key) => {
                obj[key] = attrs[key];
                return obj;
            }, {} as Partial<typeof attrs>);

        for (const [key, value] of Object.entries(filtered)) {
            if (key === 'vehicle_engine_size') {
                titleString.push(`${value.valueDisplayName} (cc)`);
            } else if (key === 'vehicle_doors') {
                titleString.push(`${value.valueDisplayName} doors`);
            } else {
                titleString.push(value.valueDisplayName);
            }
        }

        title.value = titleString.join(', ');
    }

    checkSubmitState() {
        const license = this.elLicense.value;
        return license.length > 0;
    }

    createSummary(data: VrnResponseAttributes) {
        this.elVrnSummary.innerHTML = summaryTemplate(data);
    }

    handleEstimatedMileage(response: VrnResponse) {
        const mileage = response.attributes?.vehicle_mileage
            ? response.attributes.vehicle_mileage.value
            : null;
        const estimate = response.attributes?.vehicle_estimated_mileage
            ? response.attributes.vehicle_estimated_mileage.value
            : null;

        if (estimate && mileage === estimate) {
            this.mileageData?.classList.remove('hide-fully');
        } else {
            this.mileageData?.classList.add('hide-fully');
        }
    }

    /** ℹ️ Invoke when respective DOM ready */
    init() {
        this.setConfig();
        this.setSubmitState();
        this.submitButton = new SubmitButton(this.elSubmit, dtlSyi.form!);
    }

    keyUpSubmit = async (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            await this.vrnLookup(e);
        }
    };

    /**
     * Watches for changes to mileage input and clones the data into the form
     */
    mileageValueChange = (e: KeyboardEvent) => {
        const mileage = e.target as HTMLInputElement | null;
        if (mileage && mileage.value) {
            if (mileage === this.elVrnMileage) {
                this.elManualVrnMileage!.value = mileage.value;
            }
            if (mileage === this.elManualVrnMileage) {
                this.elVrnMileage!.value = mileage.value;
            }
        }
        this.setSubmitState();
    };

    async modelFetch({ input, categoryId }: { input: string; categoryId: string }) {
        try {
            const { sellerUrl } = dtlSyi.dispatch(getPageStateThunk()).baseConfig;
            dtlSyi.dispatch(updateSyi({ vrnResponse: null, hasFetchVrn: false }));

            const endpoint = `${sellerUrl}/ajax/vrn?${new URLSearchParams({ input, categoryId })}`;

            const startDateTime = new Date().getTime();

            const fetchResponse = await fetch(endpoint, {
                method: 'GET',
                credentials: 'include', // For cross-origin Seller Request
            });

            const endDateTime = new Date().getTime();

            const pageState = dtlSyi.dispatch(getPageStateThunk());

            httpMetrics.registerHttpEvent(
                'seller',
                'GET',
                '/ajax/vrn',
                fetchResponse.status,
                endDateTime - startDateTime,
                {
                    appViewDevice: pageState.page.appViewDevice,
                }
            );

            const vrnResponse = (await fetchResponse.json()) as VrnResponse;

            if (fetchResponse.ok) {
                this.updateFields(vrnResponse);
                dtlSyi.dispatch(updateSyi({ vrnResponse, hasFetchVrn: true }));
                vrnResponse?.state !== 'FOUND' && trackV2('VRNFindFail');
            } else {
                dtlSyi.dispatch(updateSyi({ vrnResponse, hasFetchVrn: true }));
                trackV2('VRNFindFail');
            }
        } catch (e) {
             
            console.error(e);
            dtlSyi.dispatch(updateSyi({ vrnResponse: null, hasFetchVrn: true }));
            trackV2('VRNFindFail');
        }
    }

    /**
     * Load the config for this endpoint
     */
    setConfig() {
        if (this.elManualVrnMileage instanceof HTMLElement) {
            this.elManualVrnMileage.removeEventListener('keyup', this.mileageValueChange);
            this.elManualVrnMileage.addEventListener('keyup', this.mileageValueChange);
        }
        if (this.elVrnMileage instanceof HTMLElement) {
            this.elVrnMileage.removeEventListener('keyup', this.mileageValueChange);
            this.elVrnMileage.addEventListener('keyup', this.mileageValueChange);
        }
    }

    /**
     * Enables and disables vrm lookup button based on entered values
     */
    setSubmitState = () => {
        if (this.checkSubmitState()) {
            this.elSubmit.classList.remove('is-disabled');
        } else {
            this.elSubmit.classList.add('is-disabled');
        }
    };

    translateResponseForFields(response) {
        const translatedResponse = {} as Record<string, string>;
        const attributeKeys = Object.keys(response);
        attributeKeys.forEach(function (key) {
            translatedResponse[`attributes[${key}]`] = response[key].value;
        });
        return translatedResponse;
    }

    /**
     * Passes the form fields to form class to update all the correct fields
     */
    async updateFields(response: VrnResponse) {
        if (response.attributes) {
            const translatedResponseForFields = this.translateResponseForFields(
                response.attributes
            );

            this.handleEstimatedMileage(response);
            this.addToTitle(response.attributes);

            this.elValidation?.classList.add('hide-visually');

            dtlSyi.form!.updateElements(translatedResponseForFields);
            dtlSyi.submitDraft();

            this.createSummary(response.attributes);
            trackV2('VRNFindSuccess');
        } else {
            dtlSyi.dispatch(updateSyi({ vrnResponse: response, hasFetchVrn: true }));
        }
    }

    /**
     * Perform the fetch to get the information
     * @function
     */
    vrnLookup = async (event: React.KeyboardEvent | React.MouseEvent) => {
        const license = this.elLicense.value;
        //var vrnMileage = this.elVrnMileage.value;

        event.preventDefault();
        event.stopPropagation();

        if (!this.checkSubmitState()) {
            return;
        }

        // this.RADIO.trigger('form.processing'); // 🚧
        await this.modelFetch({
            input: license,
            //mileage: vrnMileage,
            categoryId: this.categoryId,
        });
    };
}

export interface VrnResponse {
    state: 'FOUND' | 'NOT_FOUND';
    attributes?: VrnResponseAttributes;
    priceGuidance?: PriceGuidance;
}

interface VrnResponseAttributes {
    motorbike_make: {
        attributeName: 'motorbike_make';
        attributeDisplayName: 'Motorbike Make'; // 🚧
        value: string;
        valueDisplayName: string;
    };
    vrn: {
        attributeName: 'vrn';
        attributeDisplayName: 'Vehicle Registration Number';
        /** e.g. `'bd08dzk'` */
        value: string;
        /** e.g. `'bd08dzk'` */
        valueDisplayName: string;
    };
    vehicle_estimated_mileage: {
        attributeName: 'vehicle_estimated_mileage';
        attributeDisplayName: 'Vehicle Estimated Mileage'; // 🚧
        value: string;
        valueDisplayName: string;
    };
    vehicle_make: {
        attributeName: 'vehicle_make';
        attributeDisplayName: 'Make';
        /** e.g. `'audi'` */
        value: string;
        /** e.g. `'Audi'` */
        valueDisplayName: string;
    };
    vehicle_mileage: {
        attributeName: 'vehicle_mileage';
        attributeDisplayName: 'Vehicle Mileage'; // 🚧
        value: string;
        valueDisplayName: string;
    };
    vehicle_model: {
        attributeName: 'vehicle_model';
        attributeDisplayName: 'Model';
        /** e.g. `'A3'` */
        value: string;
        /** e.g. `'A3'` */
        valueDisplayName: string;
    };
    vehicle_body_type: {
        attributeName: 'vehicle_body_type';
        attributeDisplayName: 'Body type';
        /** e.g. `'convertible'` */
        value: string;
        /** e.g. `'Convertible'` */
        valueDisplayName: string;
    };
    vehicle_colour: {
        attributeName: 'vehicle_colour';
        attributeDisplayName: 'Colour';
        /** e.g. `'black'` */
        value: string;
        /** e.g. `'Black'` */
        valueDisplayName: string;
    };
    vehicle_registration_year: {
        attributeName: 'vehicle_registration_year';
        attributeDisplayName: 'Year';
        /** e.g. `'2008'` */
        value: string;
        /** e.g. `'2008'` */
        valueDisplayName: string;
    };
    vehicle_fuel_type: {
        attributeName: 'vehicle_fuel_type';
        attributeDisplayName: 'Fuel type';
        /** e.g. `'diesel'` */
        value: string;
        /** e.g. `'Diesel'` */
        valueDisplayName: string;
    };
    vehicle_transmission: {
        attributeName: 'vehicle_transmission';
        attributeDisplayName: 'Transmission';
        /** e.g. `'manual'` */
        value: string;
        /** e.g. `'Manual'` */
        valueDisplayName: string;
    };
    vehicle_engine_size: {
        attributeName: 'vehicle_engine_size';
        attributeDisplayName: 'Engine size';
        /** e.g. `'1896'` */
        value: string;
        /** e.g. `'1896'` */
        valueDisplayName: string;
    };
    vehicle_doors: {
        attributeName: 'vehicle_doors';
        attributeDisplayName: 'Doors';
        /** e.g. `'2'` */
        value: string;
        /** e.g. `'2'` */
        valueDisplayName: string;
    };
}

function summaryTemplate(data: VrnResponseAttributes) {
    return `
    
<p>Vehicle details found for:
    <span class="txt-emphasis">
        ${escape(data.vehicle_make?.valueDisplayName || '')}
        
        ${escape(data.motorbike_make?.valueDisplayName || '')}
        
        ${escape(data.vehicle_model?.valueDisplayName || '')}
    </span>
</p>

<div class="grid-row">
    <div class="grid-col-12 grid-col-l-6">
        <dl class="dl-attribute-list attribute-list1">
            ${
                (data.vehicle_fuel_type &&
                    `<dt>Fuel type</dt>
                <dd>${escape(data.vehicle_fuel_type.valueDisplayName || '')}</dd>`) ||
                ''
            }
            ${
                (data.vehicle_engine_size &&
                    `<dt>Engine size (l)</dt>
                <dd>${escape(data.vehicle_engine_size.valueDisplayName || '')}</dd>`) ||
                ''
            }
            ${
                (data.vehicle_body_type &&
                    `<dt>Body type</dt>
                <dd>${escape(data.vehicle_body_type.valueDisplayName || '')}</dd>`) ||
                ''
            }
        </dl>
    </div>

    <div class="grid-col-12 grid-col-l-6">
        <dl class="dl-attribute-list attribute-list2">
            ${
                (data.vehicle_registration_year &&
                    `<dt>Year of manufacture</dt>
                <dd>${escape(data.vehicle_registration_year.valueDisplayName || '')}</dd>`) ||
                ''
            }
            
            ${
                (data.vehicle_colour &&
                    `<dt>Colour</dt>
                <dd>${escape(data.vehicle_colour.valueDisplayName || '')}</dd>`) ||
                ''
            }
            
            ${
                (data.vehicle_doors &&
                    `<dt>Doors</dt>
                <dd>${escape(data.vehicle_doors.valueDisplayName || '')}</dd>`) ||
                ''
            }
            
            ${
                (data.vehicle_transmission &&
                    `<dt>Gearbox</dt>
                <dd>${escape(data.vehicle_transmission.valueDisplayName || '')}</dd>`) ||
                ''
            }
        </dl>
    </div>
</div>
    
`.trim();
}
